/**
 * Lazy Expert Lookup - On-Demand Vanity URL Validation
 * 
 * Provides async validation for expert vanity URLs without pre-loading
 * all 18k+ URLs at boot time. Used when a single-segment slug is not
 * found in routes, pages, or locations.
 * 
 * NO CACHING: Vanity URLs must be accessible immediately after creation.
 * Performance: Single indexed query ~5-15ms (acceptable for real-time)
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';
import { ORPHAN_SENTINEL_UUID } from '@/constants/config';

interface ExpertVanityResult {
  userId: string;
}

/** Sentinel vanity URL for editorial content — always resolves without profile_active check */
const SENTINEL_VANITY_URL = 'a';

/**
 * Resolve an expert vanity URL to the user's actual UUID.
 * 
 * Used as a fallback when a slug is not found in localization_metadata
 * (e.g., newly registered profiles before the auto-seed cron runs).
 * 
 * @param slug - The URL slug to check
 * @returns Promise<ExpertVanityResult | null> - userId if found, null otherwise
 */
export async function resolveExpertVanityUrl(slug: string): Promise<ExpertVanityResult | null> {
  if (!slug || slug.length === 0) {
    return null;
  }

  // Sentinel user (editorial team) — bypass profile_active check
  if (slug === SENTINEL_VANITY_URL) {
    return { userId: ORPHAN_SENTINEL_UUID };
  }

  const startTime = performance.now();
  
  try {
    const { data, error } = await (supabase.from as Function)('users_profiles')
      .select('id, vanity_url')
      .eq('vanity_url', slug)
      .eq('profile_active', true)
      .maybeSingle();

    if (error) {
      logger.warn('[expertLookup] Query error', { slug, error: error.message });
      return null;
    }

    const queryTime = performance.now() - startTime;
    
    if (!data) {
      logger.debug('[expertLookup] Not found', { slug, queryTimeMs: Math.round(queryTime) });
      return null;
    }

    logger.debug('[expertLookup] Resolved', { 
      slug, 
      userId: data.id,
      queryTimeMs: Math.round(queryTime),
    });

    return { userId: data.id };
  } catch (err) {
    logger.error('[expertLookup] Unexpected error', { slug, error: err });
    return null;
  }
}

/**
 * Check if a slug is a valid expert vanity URL (backward-compatible boolean API)
 * 
 * @deprecated Prefer resolveExpertVanityUrl() which returns the userId
 */
export async function isExpertVanityUrl(slug: string): Promise<boolean> {
  const result = await resolveExpertVanityUrl(slug);
  return result !== null;
}
